// 文書ストア。Document (JSON) の唯一の変更口。
//
// 規約:
// - Document を書き換えるのは mutate() の draft 内だけ。他のファイルから
//   doc.value を代入しない (scripts のテストで grep 検査する)。
// - ピクセルの書き込みは mutate() が渡す PixelWriter 経由だけ。
//   これで履歴 (タイルパッチ) が自動で集まる。
// - ドラッグやスライダーは beginTransaction() で 1 履歴にまとめる。
// - ストアに入るのは JSON だけ。PixelSurface は pixelStore (非リアクティブ)。

import { defineStore } from 'pinia'
import { computed, ref, shallowRef, toRaw } from 'vue'
import type { Document, Layer, LayerId, RasterLayer, BlendMode, GroupBlendMode, Adjustment, AdjustmentKind, FillPaint, TextLayer, TextStyle, LayerStyle, ShapeLayer, ShapeGeometry, SmartLayer, BlendIf, LayerCompState } from '@/types/document'
import type { Pt, Rect } from '@/types/geometry'
import { pixelStore, type PixelWriter, type SurfacePatch } from '@/pixels/pixelStore'
import { PixelSurface, type TilePatch } from '@/pixels/pixelSurface'
import { surfaceFromRgba, solidRgba } from '@/pixels/imageDataUtil'
import { TILE_SIZE } from '@/pixels/tile'
import { HistoryManager, estimateEntryBytes, type HistoryLabel, type HistoryEntry } from '@/history/historyManager'
import { createEmptyDocument, createRasterLayer, createGroupLayer, createAdjustmentLayer, createFillLayer, createTextLayer, createShapeLayer, defaultAdjustment, newLayerId, newPixelId, type NewDocumentOptions, createSmartLayer } from '@/model/defaults'
import { textRaster, shapeRaster, dropRaster, smartRaster } from '@/pixels/rasterCache'
import { maskToAlpha } from '@/brush/fill'
import { findParentId, siblingsOf, referencedPixelIds, nextLayerName, flattenLayerIds } from '@/model/layerTree'
import { contentSignature, isEmptyDocument } from '@/model/signature'
import { maskToSurface, surfaceToMask } from '@/selection/selectionSurface'
import { combineMasks, invertMask, maskAt, type CombineMode, type MaskData } from '@/selection/mask'
import { colorRange } from '@/selection/colorRange'
import { computeHistogram, autoLevelsFromHistogram } from '@/color/histogram'
import { cubeToTiledRgba, type Cube3D } from '@/render/adjust/cube'
import { rasterizeShape } from '@/vector/rasterize'
import { refineMask, expandMask, type RefineOptions } from '@/selection/modify'
import { modifyMaskAsync } from '@/workers/clients'
import { sourceOverInPlace } from '@/brush/fill'
import { flipH, flipV, rotate90, rotate180, rotate270, resampleAffine, resizeImage as resizeRgba, transformedBounds, mat3Invert, type Interpolation, type RgbaImage } from '@/pixels/transform'
import { mat3Multiply, mat3Translate, mat3Apply, rectIntersect, type Mat3 } from '@/types/geometry'

/** 再描画に必要な範囲 */
export type DirtyInfo = { kind: 'all' } | { kind: 'view-only' } | { kind: 'rect'; rect: Rect }

export type ChangeListener = (info: DirtyInfo) => void
export type MutateFn = (draft: Document, px: PixelWriter) => void | DirtyInfo

interface Transaction {
  label: HistoryLabel
  before: Document
  tilePatches: TilePatch[]
  surfacePatches: SurfacePatch[]
}

function squashTilePatches(patches: TilePatch[]): TilePatch[] {
  // 同じタイルへの連続書き込みは「最初の before → 最後の after」にまとめる
  const map = new Map<string, TilePatch>()
  for (const p of patches) {
    const k = `${p.pixelId}|${p.key}`
    const prev = map.get(k)
    if (prev) map.set(k, { ...prev, after: p.after })
    else map.set(k, { ...p })
  }
  return [...map.values()].filter(p => p.before !== p.after)
}

export type Anchor = 'tl' | 't' | 'tr' | 'l' | 'c' | 'r' | 'bl' | 'b' | 'br'

export type AlignMode = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom'

export const useDocumentStore = defineStore('document', () => {
  const doc = shallowRef<Document | null>(null)
  const history = new HistoryManager()
  const historyVersion = ref(0)
  /** 文書を差し替えるたびに +1。コンポーネントの :key に使う */
  const sessionVersion = ref(0)
  const savedSignature = ref<string | null>(null)
  const listeners = new Set<ChangeListener>()
  let tx: Transaction | null = null

  // --- 購読 ---
  function onChange(fn: ChangeListener): () => void {
    listeners.add(fn)
    return () => listeners.delete(fn)
  }
  function emit(info: DirtyInfo) {
    for (const l of listeners) l(info)
  }

  // --- getters ---
  const hasDocument = computed(() => doc.value !== null)
  const canUndo = computed(() => {
    void historyVersion.value
    return history.canUndo
  })
  const canRedo = computed(() => {
    void historyVersion.value
    return history.canRedo
  })
  const signature = computed(() => (doc.value ? contentSignature(doc.value) : null))
  const isDirty = computed(() => {
    const d = doc.value
    if (!d) return false
    if (isEmptyDocument(d) && savedSignature.value === null) return false
    return signature.value !== savedSignature.value
  })
  const activeLayer = computed<Layer | undefined>(() => {
    const d = doc.value
    const id = d?.activeLayerIds[0]
    return d && id ? d.layers[id] : undefined
  })

  function requireDoc(): Document {
    if (!doc.value) throw new Error('ドキュメントが開かれていない')
    return doc.value
  }

  /** ヒストリーのスナップショット (文書の参照を保持。表面は不変なので参照だけで足りる) */
  // shallowRef: 中身 (Document / PixelSurface) をリアクティブにしない (structuredClone できなくなるため)
  const snapshots = shallowRef<{ id: string; name: string; at: number; doc: Document; surfaces: PixelSurface[] }[]>([])

  // --- GC: 文書からも履歴からも参照されない表面を解放する ---
  function collectGarbage() {
    const retain = history.retainedPixelIds()
    for (const s of snapshots.value) for (const id of referencedPixelIds(s.doc)) retain.add(id)
    if (doc.value) for (const id of referencedPixelIds(doc.value)) retain.add(id)
    if (tx) {
      for (const id of referencedPixelIds(tx.before)) retain.add(id)
      for (const p of tx.surfacePatches) retain.add(p.pixelId)
    }
    pixelStore.retainOnly(retain)
  }

  // --- 中核: mutate ---
  function mutate(label: HistoryLabel | string, fn: MutateFn): void {
    const before = requireDoc()
    const draft = structuredClone(before)
    let info: DirtyInfo = { kind: 'all' }
    // fn が例外を投げたら transact がピクセルを戻し、draft は捨てられる (何も反映しない)
    const { tilePatches, surfacePatches } = pixelStore.transact(px => {
      const r = fn(draft, px)
      if (r) info = r
    })
    const pixelChanged = tilePatches.length > 0 || surfacePatches.some(p => p.after !== null)
    if (pixelChanged) draft.pixelGeneration = before.pixelGeneration + 1
    draft.meta.updatedAt = Date.now()

    if (tx) {
      tx.tilePatches.push(...tilePatches)
      tx.surfacePatches.push(...surfacePatches)
    } else {
      const lbl: HistoryLabel = typeof label === 'string' ? { key: label } : label
      history.push({
        label: lbl,
        before,
        after: draft,
        tilePatches,
        surfacePatches,
        bytes: estimateEntryBytes(tilePatches, surfacePatches)
      })
    }
    doc.value = draft
    historyVersion.value = history.version
    if (!tx) collectGarbage()
    emit(info)
  }

  /** 履歴に積まない UI 状態の変更 (選択レイヤーなど) */
  function setActiveLayers(ids: LayerId[]): void {
    const d = requireDoc()
    const valid = ids.filter(id => d.layers[id])
    if (valid.join() === d.activeLayerIds.join()) return
    doc.value = { ...d, activeLayerIds: valid }
    emit({ kind: 'view-only' })
  }

  // --- トランザクション ---
  function beginTransaction(label: HistoryLabel | string): void {
    if (tx) throw new Error('トランザクションが既に開始されている')
    tx = {
      label: typeof label === 'string' ? { key: label } : label,
      before: requireDoc(),
      tilePatches: [],
      surfacePatches: []
    }
  }

  function commitTransaction(): void {
    if (!tx) return
    const t = tx
    tx = null
    const after = requireDoc()
    const tilePatches = squashTilePatches(t.tilePatches)
    const surfacePatches = t.surfacePatches
    const changed = after !== t.before && (JSON.stringify(after) !== JSON.stringify(t.before) || tilePatches.length > 0)
    if (changed) {
      history.push({
        label: t.label,
        before: t.before,
        after,
        tilePatches,
        surfacePatches,
        bytes: estimateEntryBytes(tilePatches, surfacePatches)
      })
    }
    historyVersion.value = history.version
    collectGarbage()
  }

  function rollbackTransaction(): void {
    if (!tx) return
    const t = tx
    tx = null
    for (let i = t.tilePatches.length - 1; i >= 0; i--) pixelStore.applyTilePatch(t.tilePatches[i]!, 'undo')
    for (let i = t.surfacePatches.length - 1; i >= 0; i--) pixelStore.applySurfacePatch(t.surfacePatches[i]!, 'undo')
    doc.value = t.before
    collectGarbage()
    emit({ kind: 'all' })
  }

  function transaction(label: HistoryLabel | string, fn: () => void): void {
    beginTransaction(label)
    try {
      fn()
      commitTransaction()
    } catch (e) {
      rollbackTransaction()
      throw e
    }
  }

  // --- undo / redo ---
  function applyEntry(e: HistoryEntry, direction: 'undo' | 'redo') {
    if (direction === 'undo') {
      for (let i = e.tilePatches.length - 1; i >= 0; i--) pixelStore.applyTilePatch(e.tilePatches[i]!, 'undo')
      for (let i = e.surfacePatches.length - 1; i >= 0; i--) pixelStore.applySurfacePatch(e.surfacePatches[i]!, 'undo')
      doc.value = e.before
    } else {
      for (const p of e.surfacePatches) pixelStore.applySurfacePatch(p, 'redo')
      for (const p of e.tilePatches) pixelStore.applyTilePatch(p, 'redo')
      doc.value = e.after
    }
    historyVersion.value = history.version
    collectGarbage()
    emit({ kind: 'all' })
  }

  function undo(): HistoryEntry | undefined {
    if (tx) return undefined
    const e = history.popUndo()
    if (e) applyEntry(e, 'undo')
    return e
  }

  function redo(): HistoryEntry | undefined {
    if (tx) return undefined
    const e = history.popRedo()
    if (e) applyEntry(e, 'redo')
    return e
  }

  function historyEntries() {
    void historyVersion.value
    return history.entries()
  }

  // --- 文書の差し替え ---
  function replaceDocument(next: Document, surfaces: Iterable<PixelSurface>): void {
    tx = null
    snapshots.value = []
    pixelStore.replaceAll(surfaces)
    history.clear()
    doc.value = next
    savedSignature.value = contentSignature(next)
    historyVersion.value = history.version
    sessionVersion.value++
    emit({ kind: 'all' })
  }

  function closeDocument(): void {
    tx = null
    snapshots.value = []
    pixelStore.replaceAll([])
    history.clear()
    doc.value = null
    savedSignature.value = null
    historyVersion.value = history.version
    sessionVersion.value++
    emit({ kind: 'all' })
  }

  function newDocument(opts: NewDocumentOptions): void {
    snapshots.value = []
    const d = createEmptyDocument(opts)
    const surfaces: PixelSurface[] = []
    const bg = d.meta.background
    if (bg !== 'transparent') {
      const [r, g, b] = bg === 'white' ? [255, 255, 255] : bg === 'black' ? [0, 0, 0] : hexToRgb(bg.hex)
      const pid = newPixelId()
      surfaces.push(surfaceFromRgba(pid, d.meta.width, d.meta.height, solidRgba(d.meta.width, d.meta.height, r, g, b, 255)))
      const layer = createRasterLayer({ id: newLayerId(), name: '背景', pixelId: pid, lock: { all: false, pixels: false, position: true, transparency: true } })
      d.layers[layer.id] = layer
      d.rootChildren.push(layer.id)
      d.activeLayerIds = [layer.id]
    }
    replaceDocument(d, surfaces)
    // 背景ありの新規文書は「未保存の変更なし」から始める
  }

  function markSaved(): void {
    if (doc.value) savedSignature.value = contentSignature(doc.value)
  }

  // --- よく使う mutation (コマンド層の土台) ---
  function insertLayer(draft: Document, layer: Layer, aboveId?: LayerId) {
    draft.layers[layer.id] = layer
    const ref = aboveId ?? draft.activeLayerIds[0]
    const siblings = ref ? siblingsOf(draft, ref) : undefined
    if (siblings && ref) {
      siblings.splice(siblings.indexOf(ref) + 1, 0, layer.id)
    } else {
      draft.rootChildren.push(layer.id)
    }
    draft.activeLayerIds = [layer.id]
  }

  function addRasterLayerFromRgba(name: string, width: number, height: number, rgba: Uint8ClampedArray, offset: Pt = { x: 0, y: 0 }, label: HistoryLabel | string = 'history.placeImage'): LayerId {
    const id = newLayerId()
    mutate(label, (draft, px) => {
      const surface = surfaceFromRgba(newPixelId(), width, height, rgba)
      px.adopt(surface)
      insertLayer(draft, createRasterLayer({ id, name, pixelId: surface.id, offset }))
    })
    return id
  }

  function addEmptyLayer(name?: string): LayerId {
    const id = newLayerId()
    mutate('history.addLayer', (draft, px) => {
      const s = px.create(newPixelId(), 4)
      insertLayer(draft, createRasterLayer({ id, name: name ?? nextLayerName(draft), pixelId: s.id }))
    })
    return id
  }

  function addAdjustmentLayer(kind: AdjustmentKind, name?: string, adjustment?: Adjustment): LayerId {
    const id = newLayerId()
    mutate('history.addAdjustment', draft => {
      insertLayer(draft, createAdjustmentLayer({ id, name: name ?? adjustmentDefaultName(kind), adjustment: adjustment ?? defaultAdjustment(kind) }))
    })
    return id
  }

  function setAdjustment(id: LayerId, adjustment: Adjustment, label: HistoryLabel | string = 'history.adjust'): void {
    mutate(label, draft => {
      const l = draft.layers[id]
      if (l && l.kind === 'adjustment') l.adjustment = adjustment
    })
  }

  function addFillLayer(fill: FillPaint, name?: string): LayerId {
    const id = newLayerId()
    mutate('history.addFill', draft => {
      insertLayer(draft, createFillLayer({ id, name: name ?? (fill.kind === 'solid' ? 'べた塗り' : 'グラデーション'), fill }))
    })
    return id
  }

  function setFill(id: LayerId, fill: FillPaint, label: HistoryLabel | string = 'history.fillProps'): void {
    mutate(label, draft => {
      const l = draft.layers[id]
      if (l && l.kind === 'fill') l.fill = fill
    })
  }

  /** レイヤースタイルを設定する (null で削除) */
  function setStyles(id: LayerId, styles: LayerStyle | null, label: HistoryLabel | string = 'history.layerStyle'): void {
    mutate(label, draft => {
      const l = draft.layers[id]
      if (!l) return
      if (styles && Object.keys(styles).length) l.styles = styles
      else delete l.styles
    })
  }

  /** コンテンツに応じた塗りつぶし (選択範囲が必要)。Worker で計算するため非同期 */
  async function contentAwareFillSelection(id?: LayerId): Promise<boolean> {
    const d = requireDoc()
    const lid = id ?? d.activeLayerIds[0]
    const layer = lid ? rasterLayer(lid) : undefined
    const mask = selectionMask()
    if (!layer || !mask || layer.lock.all || layer.lock.pixels) return false
    const s = pixelStore.get(layer.pixelId)
    if (!s) return false
    // 選択範囲 + 周囲 (パッチ探索の余地) を文書内で切り出す
    const pad = Math.max(24, Math.round(Math.max(mask.rect.w, mask.rect.h) * 1.5))
    const rect = rectIntersect({ x: mask.rect.x - pad, y: mask.rect.y - pad, w: mask.rect.w + pad * 2, h: mask.rect.h + pad * 2 }, { x: 0, y: 0, w: d.meta.width, h: d.meta.height })
    if (!rect) return false
    const local = { x: rect.x - layer.offset.x, y: rect.y - layer.offset.y, w: rect.w, h: rect.h }
    const img = s.readRect(local)
    const alpha = maskToAlpha(mask, rect)
    const gen = d.pixelGeneration
    const { retouchAsync } = await import('@/workers/clients')
    const out = await retouchAsync('fill', img, rect.w, rect.h, alpha)
    if (doc.value?.pixelGeneration !== gen || !rasterLayer(layer.id)) return false
    return writeLayerRect(layer.id, local, out, 'history.contentAwareFill')
  }

  /** ラスターレイヤーの画像を丸ごと置き換える (ワープの確定など)。docRect は新しい画像の文書座標 */
  function replaceLayerImage(id: LayerId, img: RgbaImage, docRect: Rect, label: HistoryLabel | string = 'history.warp'): boolean {
    const layer = rasterLayer(id)
    if (!layer || layer.lock.all || layer.lock.pixels) return false
    const before = layerRect(id)
    mutate(label, (draft, px) => {
      const l = draft.layers[id]
      if (!l || l.kind !== 'raster') return
      l.pixelId = replaceSurface(px, l.pixelId, img, { x: 0, y: 0 }, 4)
      l.offset = { x: docRect.x, y: docRect.y }
      const u = unionRect(before, docRect)
      return u ? { kind: 'rect', rect: u } : { kind: 'all' }
    })
    return true
  }

  /** シェイプの幾何 (塗り / 線の有無に関係なく内側) を選択範囲にする */
  function selectionFromShapeGeometry(id: LayerId, mode: CombineMode = 'new'): boolean {
    const l = doc.value?.layers[id]
    if (!l || l.kind !== 'shape') return false
    const r = rasterizeShape({ ...l, fillHex: '#000000', fillAlpha: 1, strokeHex: null })
    if (!r) return false
    const data = new Uint8ClampedArray(r.rect.w * r.rect.h)
    for (let i = 0; i < data.length; i++) data[i] = r.image.data[i * 4 + 3]!
    combineSelection({ rect: { ...r.rect }, data }, mode, 'history.select')
    return true
  }

  // --- カラールックアップ (3D LUT) ---
  /** 調整レイヤーの LUT を差し替える (タイル化した画像を表面として持つ) */
  function setColorLookup(id: LayerId, cube: Cube3D, name: string): void {
    const tiled = cubeToTiledRgba(cube)
    mutate('history.adjust', (draft, px) => {
      const l = draft.layers[id]
      if (!l || l.kind !== 'adjustment' || l.adjustment.kind !== 'color-lookup') return
      if (l.adjustment.lutPixelId) px.drop(l.adjustment.lutPixelId)
      const s = surfaceFromRgba(newPixelId(), tiled.w, tiled.h, tiled.rgba)
      px.adopt(s)
      l.adjustment = { kind: 'color-lookup', lutPixelId: s.id, size: cube.size, name }
    })
  }
  function addColorLookupLayer(cube: Cube3D, name: string): LayerId {
    const id = addAdjustmentLayer('color-lookup', name || undefined)
    setColorLookup(id, cube, name)
    return id
  }

  // --- パターン ---
  /** 選択範囲 (無ければ全体) の合成画像をパターンとして定義する */
  function definePattern(reference: { rgba: Uint8ClampedArray; width: number; height: number }, name?: string): string | undefined {
    const d = requireDoc()
    const mask = selectionMask()
    const rect = mask ? rectIntersect(mask.rect, { x: 0, y: 0, w: reference.width, h: reference.height }) : { x: 0, y: 0, w: reference.width, h: reference.height }
    if (!rect || rect.w < 1 || rect.h < 1) return undefined
    const rgba = new Uint8ClampedArray(rect.w * rect.h * 4)
    for (let y = 0; y < rect.h; y++) rgba.set(reference.rgba.subarray(((rect.y + y) * reference.width + rect.x) * 4, ((rect.y + y) * reference.width + rect.x + rect.w) * 4), y * rect.w * 4)
    const id = 'pat_' + newLayerId().slice(3)
    mutate('history.definePattern', (draft, px) => {
      const s = surfaceFromRgba(newPixelId(), rect.w, rect.h, rgba)
      px.adopt(s)
      draft.patterns.push({ id, name: name ?? `パターン ${d.patterns.length + 1}`, pixelId: s.id, w: rect.w, h: rect.h })
      return { kind: 'view-only' }
    })
    return id
  }
  function removePattern(id: string): void {
    mutate('history.removePattern', (draft, px) => {
      const p = draft.patterns.find(x => x.id === id)
      if (!p) return
      px.drop(p.pixelId)
      draft.patterns = draft.patterns.filter(x => x.id !== id)
      return { kind: 'view-only' }
    })
  }
  /** 選択範囲 (無ければ全体) をパターンで塗る (CPU でタイル) */
  function fillSelectionWithPattern(patternId: string, opacity = 1, scale = 1): boolean {
    const d = requireDoc()
    const aid = d.activeLayerIds[0]
    const layer = aid ? rasterLayer(aid) : undefined
    const pat = d.patterns.find(p => p.id === patternId)
    const ps = pat ? pixelStore.get(pat.pixelId) : undefined
    const pb = ps?.contentBounds()
    if (!layer || !pat || !ps || !pb) return false
    const tile = ps.readRect(pb)
    const mask = selectionMask()
    const rect = mask ? mask.rect : { x: 0, y: 0, w: d.meta.width, h: d.meta.height }
    const alpha = maskToAlpha(mask, rect)
    const rgba = new Uint8ClampedArray(rect.w * rect.h * 4)
    const tw = pb.w * scale, th = pb.h * scale
    for (let y = 0; y < rect.h; y++) {
      const sy = Math.floor(((((rect.y + y) % th) + th) % th) / scale)
      for (let x = 0; x < rect.w; x++) {
        const sx = Math.floor(((((rect.x + x) % tw) + tw) % tw) / scale)
        const si = (Math.min(pb.h - 1, sy) * pb.w + Math.min(pb.w - 1, sx)) * 4, o = (y * rect.w + x) * 4
        rgba[o] = tile[si]!; rgba[o + 1] = tile[si + 1]!; rgba[o + 2] = tile[si + 2]!
        rgba[o + 3] = Math.round((tile[si + 3]! * alpha[y * rect.w + x]!) / 255)
      }
    }
    return compositeIntoLayer(layer.id, rect, rgba, 'history.fill', opacity)
  }

  // --- レイヤーカンプ ---
  function captureCompStates(d: Document): Record<LayerId, LayerCompState> {
    const states: Record<LayerId, LayerCompState> = {}
    for (const id of flattenLayerIds(d)) {
      const l = d.layers[id]
      if (!l) continue
      const e: LayerCompState = { visible: l.visible }
      const o = layerOrigin(id)
      if (o) e.position = o
      e.styles = l.styles ? structuredClone(l.styles) : null
      states[id] = e
    }
    return states
  }
  function addLayerComp(name?: string): string {
    const d = requireDoc()
    const id = 'comp_' + newLayerId().slice(3)
    const states = captureCompStates(d)
    mutate('history.addComp', draft => {
      draft.comps.push({ id, name: name ?? `レイヤーカンプ ${draft.comps.length + 1}`, states })
      return { kind: 'view-only' }
    })
    return id
  }
  function updateLayerComp(id: string): void {
    const d = requireDoc()
    const states = captureCompStates(d)
    mutate('history.updateComp', draft => {
      const c = draft.comps.find(x => x.id === id)
      if (c) c.states = states
      return { kind: 'view-only' }
    })
  }
  function applyLayerComp(id: string): void {
    const d = requireDoc()
    const comp = d.comps.find(x => x.id === id)
    if (!comp) return
    beginTransaction('history.applyComp')
    try {
      for (const [lid, st] of Object.entries(comp.states)) {
        const l = doc.value!.layers[lid]
        if (!l) continue
        if (l.visible !== st.visible) toggleVisible(lid)
        if (st.position) {
          const o = layerOrigin(lid)
          if (o && (o.x !== st.position.x || o.y !== st.position.y)) setLayerOffset(lid, st.position)
        }
        if (st.styles !== undefined && l.kind !== 'group') setStyles(lid, st.styles ?? {})
      }
      commitTransaction()
    } catch (e) {
      rollbackTransaction()
      throw e
    }
  }
  function removeLayerComp(id: string): void {
    mutate('history.removeComp', draft => {
      draft.comps = draft.comps.filter(x => x.id !== id)
      return { kind: 'view-only' }
    })
  }
  function renameLayerComp(id: string, name: string): void {
    mutate('history.renameComp', draft => {
      const c = draft.comps.find(x => x.id === id)
      if (c) c.name = name
      return { kind: 'view-only' }
    })
  }

  // --- スナップショット ---
  function takeSnapshot(name?: string): string {
    const d = requireDoc()
    const id = 'snap_' + newLayerId().slice(3)
    // 表面は不変 (COW) なので参照を持つだけでよい。あとで drop されても復元時に戻せる
    const surfaces = [...referencedPixelIds(d)].map(pid => pixelStore.get(pid)).filter((s): s is PixelSurface => !!s)
    snapshots.value = [...snapshots.value, { id, name: name ?? `スナップショット ${snapshots.value.length + 1}`, at: Date.now(), doc: toRaw(d), surfaces }]
    return id
  }
  function restoreSnapshot(id: string): void {
    const s = snapshots.value.find(x => x.id === id)
    if (!s) return
    requireDoc()
    mutate('history.restoreSnapshot', (draft, px) => {
      for (const surf of s.surfaces) if (!pixelStore.get(surf.id)) px.adopt(surf)
      const next = structuredClone(toRaw(s.doc))
      for (const k of Object.keys(draft) as (keyof Document)[]) delete (draft as unknown as Record<string, unknown>)[k]
      Object.assign(draft, next)
      return { kind: 'all' }
    })
  }
  function deleteSnapshot(id: string): void {
    snapshots.value = snapshots.value.filter(x => x.id !== id)
    collectGarbage()
  }

  // --- 結合 / コピーしたレイヤー / 塗りつぶし / 境界線 / トリミング / 任意角度回転 / 自動補正 ---
  /** 文書サイズの RGBA でラスターレイヤーを作り、ids を置き換える (最上位の id の位置に入れる) */
  function replaceLayersWithRaster(ids: LayerId[], rgba: Uint8ClampedArray, name: string, label: HistoryLabel | string, keepHidden = true): LayerId {
    const d = requireDoc()
    const order = flattenLayerIds(d)
    const sorted = [...ids].sort((a, b) => order.indexOf(a) - order.indexOf(b))
    const top = sorted[sorted.length - 1]!
    const id = newLayerId()
    mutate(label, (draft, px) => {
      const surface = surfaceFromRgba(newPixelId(), draft.meta.width, draft.meta.height, rgba)
      px.adopt(surface)
      const src = draft.layers[top]
      const layer = createRasterLayer({ id, name, pixelId: surface.id, offset: { x: 0, y: 0 }, blendMode: src && src.kind !== 'group' ? src.blendMode : 'normal' })
      insertLayer(draft, layer, top)
      for (const lid of sorted) removeLayerDeep(draft, px, lid)
      if (!keepHidden) for (const lid of [...flattenLayerIds(draft)]) { const l = draft.layers[lid]; if (l && !l.visible && lid !== id) removeLayerDeep(draft, px, lid) }
      draft.activeLayerIds = [id]
    })
    return id
  }
  /** 選択範囲 (無ければレイヤー全体) を新しいレイヤーへコピー / カット */
  function newLayerViaCopy(cut: boolean): LayerId | undefined {
    const d = requireDoc()
    const aid = d.activeLayerIds[0]
    const layer = aid ? rasterLayer(aid) : undefined
    const s = layer ? pixelStore.get(layer.pixelId) : undefined
    const b = s?.contentBounds()
    if (!layer || !s || !b) return undefined
    const mask = selectionMask()
    const docRect = { x: b.x + layer.offset.x, y: b.y + layer.offset.y, w: b.w, h: b.h }
    const rect = mask ? rectIntersect(docRect, mask.rect) : docRect
    if (!rect) return undefined
    const local = { x: rect.x - layer.offset.x, y: rect.y - layer.offset.y, w: rect.w, h: rect.h }
    const data = s.readRect(local)
    if (mask) {
      const alpha = maskToAlpha(mask, rect)
      for (let i = 0; i < rect.w * rect.h; i++) data[i * 4 + 3] = Math.round((data[i * 4 + 3]! * alpha[i]!) / 255)
    }
    const id = newLayerId()
    beginTransaction(cut ? 'history.layerViaCut' : 'history.layerViaCopy')
    try {
      if (cut && !layer.lock.all && !layer.lock.pixels) clearSelectedPixels()
      mutate('history.layerViaCopy', (draft, px) => {
        const surface = surfaceFromRgba(newPixelId(), rect.w, rect.h, data)
        px.adopt(surface)
        insertLayer(draft, createRasterLayer({ id, name: `${layer.name} のコピー`, pixelId: surface.id, offset: { x: rect.x, y: rect.y } }), layer.id)
      })
      commitTransaction()
    } catch (e) {
      rollbackTransaction()
      throw e
    }
    return id
  }
  /** 選択範囲 (無ければ全体) を単色で塗る */
  function fillSelectionWithColor(hex: string, opacity = 1, label: HistoryLabel | string = 'history.fill'): boolean {
    const d = requireDoc()
    const aid = d.activeLayerIds[0]
    const layer = aid ? rasterLayer(aid) : undefined
    if (!layer) return false
    const mask = selectionMask()
    const rect = mask ? mask.rect : { x: 0, y: 0, w: d.meta.width, h: d.meta.height }
    return fillMaskWithColor(layer.id, mask, rect, hex, opacity, label)
  }
  function fillMaskWithColor(layerId: LayerId, mask: MaskData | null, rect: Rect, hex: string, opacity: number, label: HistoryLabel | string): boolean {
    const [r, g, b] = hexToRgb(hex)
    const alpha = maskToAlpha(mask, rect)
    const rgba = new Uint8ClampedArray(rect.w * rect.h * 4)
    for (let i = 0; i < rect.w * rect.h; i++) { rgba[i * 4] = r; rgba[i * 4 + 1] = g; rgba[i * 4 + 2] = b; rgba[i * 4 + 3] = alpha[i]! }
    return compositeIntoLayer(layerId, rect, rgba, label, opacity)
  }
  /** 選択範囲の境界線を描く (内側 / 中央 / 外側) */
  function strokeSelection(width: number, hex: string, position: 'inside' | 'center' | 'outside' = 'center', opacity = 1): boolean {
    const d = requireDoc()
    const aid = d.activeLayerIds[0]
    const layer = aid ? rasterLayer(aid) : undefined
    const mask = selectionMask()
    if (!layer || !mask || width <= 0) return false
    const outer = position === 'inside' ? mask : expandMask(mask, position === 'outside' ? width : width / 2)
    const inner = position === 'outside' ? mask : expandMask(mask, -(position === 'inside' ? width : width / 2))
    if (!outer) return false
    const ring = inner ? combineMasks(outer, inner, 'subtract') : outer
    if (!ring) return false
    return fillMaskWithColor(layer.id, ring, ring.rect, hex, opacity, 'history.stroke')
  }
  /** 透明部分を切り落とす (表示レイヤーの内容の外接矩形) */
  function trimTransparent(): boolean {
    const d = requireDoc()
    let r: Rect | null = null
    for (const id of flattenLayerIds(d)) {
      const l = d.layers[id]
      if (!l || !l.visible || l.kind === 'group' || l.kind === 'adjustment' || l.kind === 'fill') continue
      r = unionRect(r, layerRect(id))
    }
    const clipped = r ? rectIntersect(r, { x: 0, y: 0, w: d.meta.width, h: d.meta.height }) : null
    if (!clipped) return false
    cropDocument(clipped)
    return true
  }
  /** カンバスを任意角度で回転 (レイヤーを回転し、カンバスは回転後の外接矩形) */
  function rotateCanvasArbitrary(degrees: number): void {
    const d = requireDoc()
    const a = (degrees * Math.PI) / 180
    const c = Math.cos(a), sn = Math.sin(a)
    const W = d.meta.width, H = d.meta.height
    const nw = Math.round(Math.abs(W * c) + Math.abs(H * sn)), nh = Math.round(Math.abs(W * sn) + Math.abs(H * c))
    const m: Mat3 = mat3Multiply(mat3Translate(nw / 2, nh / 2), mat3Multiply([c, sn, 0, -sn, c, 0, 0, 0, 1], mat3Translate(-W / 2, -H / 2)))
    beginTransaction('history.rotateArbitrary')
    try {
      for (const id of flattenLayerIds(d)) {
        const l = doc.value!.layers[id]
        if (!l) continue
        if (l.kind === 'raster' && !l.lock.all && !l.lock.pixels) transformLayer(id, m, 'bicubic')
        else if (l.kind === 'shape' || l.kind === 'smart') transformLayer(id, m)
        else if (l.kind === 'text') setText(id, { position: mat3Apply(m, l.position) })
      }
      mutate('history.rotateArbitrary', (draft, px) => {
        draft.meta.width = nw
        draft.meta.height = nh
        if (draft.selection.pixelId) {
          px.drop(draft.selection.pixelId)
          draft.selection.pixelId = null
        }
        return { kind: 'all' }
      })
      commitTransaction()
    } catch (e) {
      rollbackTransaction()
      throw e
    }
  }
  /** 自動トーン補正 (チャンネル別) / 自動コントラスト (輝度) をレベル補正レイヤーとして追加 */
  function autoLevels(mode: 'tone' | 'contrast', reference: { rgba: Uint8ClampedArray; width: number; height: number }): LayerId {
    const hist = computeHistogram(reference.rgba)
    const lv = autoLevelsFromHistogram(hist, mode)
    return addAdjustmentLayer('levels', mode === 'tone' ? '自動トーン補正' : '自動コントラスト', { kind: 'levels', ...lv })
  }

  // --- 色域指定 / 選択範囲を調整 ---
  function selectColorRange(hex: string, fuzziness: number, invert: boolean, mode: CombineMode = 'new', reference?: { rgba: Uint8ClampedArray; width: number; height: number }): void {
    const d = requireDoc()
    const [cr, cg, cb] = hexToRgb(hex)
    let rgba: Uint8ClampedArray, rect: Rect
    if (reference) {
      rgba = reference.rgba
      rect = { x: 0, y: 0, w: reference.width, h: reference.height }
    } else {
      const id = d.activeLayerIds[0]
      const layer = id ? rasterLayer(id) : undefined
      const s = layer ? pixelStore.get(layer.pixelId) : undefined
      if (!layer || !s) return
      rect = { x: 0, y: 0, w: d.meta.width, h: d.meta.height }
      rgba = s.readRect({ x: -layer.offset.x, y: -layer.offset.y, w: rect.w, h: rect.h })
    }
    combineSelection(colorRange(rgba, rect, { r: cr, g: cg, b: cb, fuzziness, invert }), mode, 'history.colorRange')
  }
  function refineSelection(o: RefineOptions): void {
    const cur = selectionMask()
    if (!cur) return
    setSelection(refineMask(cur, o), 'history.refineEdge')
  }

  // --- 整列 / 分布 ---
  /** レイヤーの「位置」(raster: offset, text: position, shape: 平行移動成分) */
  function layerOrigin(id: LayerId): Pt | null {
    const l = doc.value?.layers[id]
    if (!l) return null
    if (l.kind === 'raster') return { ...l.offset }
    if (l.kind === 'text') return { ...l.position }
    if (l.kind === 'shape' || l.kind === 'smart') return { x: l.transform[6], y: l.transform[7] }
    return null
  }
  function moveLayerBy(id: LayerId, dx: number, dy: number): void {
    const o = layerOrigin(id)
    if (!o || (!dx && !dy)) return
    setLayerOffset(id, { x: o.x + dx, y: o.y + dy })
  }
  /**
   * 整列。基準は選択範囲があればその矩形、2 つ以上のレイヤーならその外接矩形、1 つならカンバス。
   */
  function alignLayers(ids: LayerId[], mode: AlignMode): void {
    const d = requireDoc()
    const items = ids.map(id => ({ id, rect: layerRect(id) })).filter((i): i is { id: LayerId; rect: Rect } => !!i.rect)
    if (!items.length) return
    const sel = selectionMask()
    let ref: Rect
    if (sel) ref = sel.rect
    else if (items.length >= 2) {
      let r: Rect | null = null
      for (const it of items) r = unionRect(r, it.rect)
      ref = r!
    } else ref = { x: 0, y: 0, w: d.meta.width, h: d.meta.height }
    beginTransaction('history.alignLayers')
    try {
      for (const it of items) {
        const r = it.rect
        let dx = 0, dy = 0
        switch (mode) {
          case 'left': dx = ref.x - r.x; break
          case 'hcenter': dx = Math.round(ref.x + ref.w / 2 - (r.x + r.w / 2)); break
          case 'right': dx = ref.x + ref.w - (r.x + r.w); break
          case 'top': dy = ref.y - r.y; break
          case 'vcenter': dy = Math.round(ref.y + ref.h / 2 - (r.y + r.h / 2)); break
          case 'bottom': dy = ref.y + ref.h - (r.y + r.h); break
        }
        moveLayerBy(it.id, dx, dy)
      }
      commitTransaction()
    } catch (e) {
      rollbackTransaction()
      throw e
    }
  }
  /** 分布: 両端のレイヤーはそのまま、間のレイヤーの中心を等間隔にする (3 つ以上) */
  function distributeLayers(ids: LayerId[], axis: 'h' | 'v'): void {
    const items = ids.map(id => ({ id, rect: layerRect(id) })).filter((i): i is { id: LayerId; rect: Rect } => !!i.rect)
    if (items.length < 3) return
    const center = (r: Rect) => (axis === 'h' ? r.x + r.w / 2 : r.y + r.h / 2)
    items.sort((a, b) => center(a.rect) - center(b.rect))
    const first = center(items[0]!.rect), last = center(items[items.length - 1]!.rect)
    const step = (last - first) / (items.length - 1)
    beginTransaction('history.distributeLayers')
    try {
      for (let i = 1; i < items.length - 1; i++) {
        const target = first + step * i
        const delta = Math.round(target - center(items[i]!.rect))
        moveLayerBy(items[i]!.id, axis === 'h' ? delta : 0, axis === 'h' ? 0 : delta)
      }
      commitTransaction()
    } catch (e) {
      rollbackTransaction()
      throw e
    }
  }

  // --- アルファチャンネル (選択範囲の保存 / 読み込み) ---
  function saveSelectionAsChannel(name?: string): string | undefined {
    const d = requireDoc()
    if (!d.selection.pixelId) return undefined
    const src = pixelStore.get(d.selection.pixelId)
    if (!src) return undefined
    const id = 'ch_' + newLayerId().slice(3)
    mutate('history.saveChannel', (draft, px) => {
      const s = src.fork(newPixelId())
      px.adopt(s)
      draft.channels.push({ id, name: name ?? `アルファチャンネル ${draft.channels.length + 1}`, pixelId: s.id })
      return { kind: 'view-only' }
    })
    return id
  }
  function loadChannelAsSelection(id: string, mode: CombineMode = 'new'): void {
    const d = requireDoc()
    const ch = d.channels.find(c => c.id === id)
    const s = ch ? pixelStore.get(ch.pixelId) : undefined
    if (!s) return
    combineSelection(surfaceToMask(s), mode, 'history.loadChannel')
  }
  function removeChannel(id: string): void {
    mutate('history.removeChannel', (draft, px) => {
      const ch = draft.channels.find(c => c.id === id)
      if (!ch) return
      px.drop(ch.pixelId)
      draft.channels = draft.channels.filter(c => c.id !== id)
      return { kind: 'view-only' }
    })
  }
  function renameChannel(id: string, name: string): void {
    mutate('history.renameChannel', draft => {
      const ch = draft.channels.find(c => c.id === id)
      if (ch) ch.name = name
      return { kind: 'view-only' }
    })
  }

  /** クイックマスク: カバレッジ (文書座標 rect の 0..255) を選択範囲へ加える / 引く */
  function paintSelection(rect: Rect, coverage: Uint8ClampedArray, subtract: boolean, opacity = 1): void {
    const d = requireDoc()
    const cur = selectionMask()
    const data = new Uint8ClampedArray(rect.w * rect.h)
    for (let i = 0; i < data.length; i++) data[i] = Math.round(coverage[i]! * opacity)
    const next = combineMasks(cur, { rect, data }, subtract ? 'subtract' : 'add')
    void d
    setSelection(next, 'history.quickMask')
  }

  // --- ガイド ---
  function addGuide(axis: 'x' | 'y', position: number): string {
    const id = 'g_' + newLayerId().slice(3)
    mutate('history.addGuide', draft => {
      draft.guides.push({ id, axis, position: Math.round(position) })
      return { kind: 'view-only' }
    })
    return id
  }
  function moveGuide(id: string, position: number): void {
    mutate('history.moveGuide', draft => {
      const g = draft.guides.find(x => x.id === id)
      if (g) g.position = Math.round(position)
      return { kind: 'view-only' }
    })
  }
  function removeGuide(id: string): void {
    mutate('history.removeGuide', draft => {
      draft.guides = draft.guides.filter(x => x.id !== id)
      return { kind: 'view-only' }
    })
  }
  function clearGuides(): void {
    if (!doc.value?.guides.length) return
    mutate('history.clearGuides', draft => {
      draft.guides = []
      return { kind: 'view-only' }
    })
  }

  // --- シェイプ ---
  function addShapeLayer(geometry: ShapeGeometry, props: Partial<Pick<ShapeLayer, 'fillHex' | 'fillAlpha' | 'strokeHex' | 'strokeWidth' | 'strokeAlpha' | 'strokeAlign' | 'name'>> = {}): LayerId {
    const id = newLayerId()
    mutate('history.addShape', draft => {
      insertLayer(draft, createShapeLayer({ id, geometry, ...props }))
    })
    return id
  }

  function setShape(id: LayerId, patch: Partial<Pick<ShapeLayer, 'geometry' | 'fillHex' | 'fillAlpha' | 'strokeHex' | 'strokeWidth' | 'strokeAlpha' | 'strokeAlign' | 'transform'>>, label: HistoryLabel | string = 'history.editShape'): void {
    mutate(label, draft => {
      const l = draft.layers[id]
      if (!l || l.kind !== 'shape') return
      const before = shapeRect(id)
      Object.assign(l, patch)
      const after = shapeRect(id, l)
      const u = unionRect(before, after)
      return u ? { kind: 'rect', rect: u } : { kind: 'all' }
    })
  }

  // --- スマートオブジェクト ---
  function smartRect(id: LayerId, layer?: SmartLayer): Rect | null {
    const l = layer ?? doc.value?.layers[id]
    if (!l || l.kind !== 'smart') return null
    return smartRaster(l, pixelStore.get(l.sourceId))?.rect ?? null
  }
  /** ラスターレイヤーをスマートオブジェクトへ (画素はそのまま元画像になる) */
  function convertToSmartObject(id: LayerId): void {
    const l = rasterLayer(id)
    if (!l || l.lock.all) return
    const s = pixelStore.get(l.pixelId)
    const b = s?.contentBounds()
    if (!s || !b) return
    mutate('history.convertToSmart', draft => {
      const t = draft.layers[id]
      if (!t || t.kind !== 'raster') return
      const smart = createSmartLayer({ id, name: t.name, sourceId: t.pixelId, sourceRect: b, transform: mat3Translate(t.offset.x, t.offset.y), visible: t.visible, opacity: t.opacity, fillOpacity: t.fillOpacity, blendMode: t.blendMode, clipToBelow: t.clipToBelow, lock: t.lock })
      if (t.mask) smart.mask = t.mask
      if (t.styles) smart.styles = t.styles
      draft.layers[id] = smart
      return { kind: 'view-only' }
    })
  }
  /** スマートオブジェクトをラスターレイヤーへ (現在の変形結果を焼き込む) */
  function rasterizeSmartLayer(id: LayerId): void {
    const l = doc.value?.layers[id]
    if (!l || l.kind !== 'smart') return
    const r = smartRaster(l, pixelStore.get(l.sourceId))
    mutate('history.rasterizeSmart', (draft, px) => {
      const t = draft.layers[id]
      if (!t || t.kind !== 'smart') return
      const s = px.create(newPixelId(), 4)
      if (r) px.writeRect(s.id, { x: 0, y: 0, w: r.rect.w, h: r.rect.h }, r.surface.readRect({ x: 0, y: 0, w: r.rect.w, h: r.rect.h }))
      const raster = createRasterLayer({ id, name: t.name, pixelId: s.id, offset: r ? { x: r.rect.x, y: r.rect.y } : { x: 0, y: 0 }, visible: t.visible, opacity: t.opacity, fillOpacity: t.fillOpacity, blendMode: t.blendMode, clipToBelow: t.clipToBelow, lock: t.lock })
      if (t.mask) raster.mask = t.mask
      if (t.styles) raster.styles = t.styles
      draft.layers[id] = raster
      dropRaster(id)
      return { kind: 'view-only' }
    })
  }
  function setSmart(id: LayerId, patch: Partial<Pick<SmartLayer, 'transform' | 'interpolation'>>, label: HistoryLabel | string = 'history.editSmart'): void {
    mutate(label, draft => {
      const l = draft.layers[id]
      if (!l || l.kind !== 'smart') return
      const before = smartRect(id)
      Object.assign(l, patch)
      const after = smartRect(id, l)
      const u = unionRect(before, after)
      return u ? { kind: 'rect', rect: u } : { kind: 'all' }
    })
  }
  /** 変形をリセット (元画像の位置は左上を保つ) */
  function resetSmartTransform(id: LayerId): void {
    const l = doc.value?.layers[id]
    if (!l || l.kind !== 'smart') return
    const cur = smartRect(id)
    setSmart(id, { transform: mat3Translate(cur ? cur.x - l.sourceRect.x : 0, cur ? cur.y - l.sourceRect.y : 0) }, 'history.resetSmart')
  }
  /** 元画像を差し替える (サイズが違えば中央に合わせる) */
  function replaceSmartContents(id: LayerId, width: number, height: number, rgba: Uint8ClampedArray): void {
    const l = doc.value?.layers[id]
    if (!l || l.kind !== 'smart') return
    mutate('history.replaceSmart', (draft, px) => {
      const t = draft.layers[id]
      if (!t || t.kind !== 'smart') return
      const s = px.create(newPixelId(), 4)
      px.writeRect(s.id, { x: 0, y: 0, w: width, h: height }, rgba)
      // 旧 sourceRect の中心が新しい中心に来るように平行移動を補正
      const ocx = t.sourceRect.x + t.sourceRect.w / 2, ocy = t.sourceRect.y + t.sourceRect.h / 2
      const ncx = width / 2, ncy = height / 2
      t.sourceId = s.id
      t.sourceRect = { x: 0, y: 0, w: width, h: height }
      t.transform = mat3Multiply(t.transform, mat3Translate(ocx - ncx, ocy - ncy))
      dropRaster(id)
      return { kind: 'all' }
    })
  }

  function shapeRect(id: LayerId, layer?: ShapeLayer): Rect | null {
    const l = layer ?? doc.value?.layers[id]
    if (!l || l.kind !== 'shape') return null
    return shapeRaster(l)?.rect ?? null
  }

  /** シェイプレイヤーをラスターレイヤーに変換する */
  function rasterizeShapeLayer(id: LayerId): void {
    const l = doc.value?.layers[id]
    if (!l || l.kind !== 'shape') return
    const r = shapeRaster(l)
    mutate('history.rasterizeShape', (draft, px) => {
      const t = draft.layers[id]
      if (!t || t.kind !== 'shape') return
      const s = px.create(newPixelId(), 4)
      if (r) px.writeRect(s.id, { x: 0, y: 0, w: r.rect.w, h: r.rect.h }, r.surface.readRect({ x: 0, y: 0, w: r.rect.w, h: r.rect.h }))
      const raster = createRasterLayer({ id, name: t.name, pixelId: s.id, offset: r ? { x: r.rect.x, y: r.rect.y } : { x: 0, y: 0 }, visible: t.visible, opacity: t.opacity, fillOpacity: t.fillOpacity, blendMode: t.blendMode, clipToBelow: t.clipToBelow, lock: t.lock })
      if (t.mask) raster.mask = t.mask
      if (t.styles) raster.styles = t.styles
      draft.layers[id] = raster
      dropRaster(id)
    })
  }

  /** シェイプ (またはテキスト) の不透明部分を選択範囲にする */
  function selectionFromLayerAlpha(id: LayerId, mode: CombineMode = 'new'): void {
    const l = doc.value?.layers[id]
    if (!l) return
    let src: { surface: PixelSurface; rect: Rect; offset: Pt } | null = null
    if (l.kind === 'shape') src = shapeRaster(l)
    else if (l.kind === 'smart') src = smartRaster(l, pixelStore.get(l.sourceId))
    else if (l.kind === 'text') src = textRaster(l)
    else if (l.kind === 'raster') {
      const s = pixelStore.get(l.pixelId)
      const b = s?.contentBounds()
      if (s && b) src = { surface: s, rect: { x: b.x + l.offset.x, y: b.y + l.offset.y, w: b.w, h: b.h }, offset: l.offset }
    }
    if (!src) return
    const local = l.kind === 'raster' ? { x: src.rect.x - src.offset.x, y: src.rect.y - src.offset.y, w: src.rect.w, h: src.rect.h } : { x: 0, y: 0, w: src.rect.w, h: src.rect.h }
    const rgba = src.surface.readRect(local)
    const data = new Uint8ClampedArray(src.rect.w * src.rect.h)
    for (let i = 0; i < data.length; i++) data[i] = rgba[i * 4 + 3]!
    combineSelection({ rect: { ...src.rect }, data }, mode, 'history.select')
    void maskToAlpha
  }

  // --- テキスト ---
  function addTextLayer(position: Pt, text: string, style: Partial<TextStyle> = {}, boxWidth?: number): LayerId {
    const id = newLayerId()
    mutate('history.addText', draft => {
      const l = createTextLayer({ id, position, text, style: { ...createTextLayer({ position }).style, ...style } })
      if (boxWidth) l.boxWidth = boxWidth
      insertLayer(draft, l)
    })
    return id
  }

  function setText(id: LayerId, patch: Partial<Pick<TextLayer, 'text' | 'position' | 'boxWidth'>> & { style?: Partial<TextStyle>; warp?: TextLayer['warp'] | null }, label: HistoryLabel | string = 'history.editText'): void {
    mutate(label, draft => {
      const l = draft.layers[id]
      if (!l || l.kind !== 'text') return
      if (patch.warp !== undefined) {
        if (patch.warp === null) delete l.warp
        else l.warp = { ...patch.warp }
      }
      const before = textRect(id)
      if (patch.text !== undefined) {
        l.text = patch.text
        // 名前が本文由来ならタイトルも追従
        const first = patch.text.split('\n')[0]!.slice(0, 30)
        if (first) l.name = first
      }
      if (patch.position) l.position = { x: patch.position.x, y: patch.position.y }
      if (patch.boxWidth !== undefined) {
        if (patch.boxWidth) l.boxWidth = patch.boxWidth
        else delete l.boxWidth
      }
      if (patch.style) l.style = { ...l.style, ...patch.style }
      const after = textRect(id, l)
      const u = unionRect(before, after)
      return u ? { kind: 'rect', rect: u } : { kind: 'all' }
    })
  }

  /** テキストレイヤーのラスタ矩形 (文書座標) */
  function textRect(id: LayerId, layer?: TextLayer): Rect | null {
    const l = layer ?? doc.value?.layers[id]
    if (!l || l.kind !== 'text') return null
    return textRaster(l)?.rect ?? null
  }

  /** テキストレイヤーをラスターレイヤーに変換する */
  function rasterizeTextLayer(id: LayerId): void {
    const l = doc.value?.layers[id]
    if (!l || l.kind !== 'text') return
    const r = textRaster(l)
    mutate('history.rasterizeText', (draft, px) => {
      const t = draft.layers[id]
      if (!t || t.kind !== 'text') return
      const s = px.create(newPixelId(), 4)
      if (r) px.writeRect(s.id, { x: 0, y: 0, w: r.rect.w, h: r.rect.h }, r.surface.readRect({ x: 0, y: 0, w: r.rect.w, h: r.rect.h }))
      const raster = createRasterLayer({ id, name: t.name, pixelId: s.id, offset: r ? { x: r.rect.x, y: r.rect.y } : { x: 0, y: 0 }, visible: t.visible, opacity: t.opacity, fillOpacity: t.fillOpacity, blendMode: t.blendMode, clipToBelow: t.clipToBelow, lock: t.lock })
      if (t.mask) raster.mask = t.mask
      draft.layers[id] = raster
      dropRaster(id)
    })
  }

  function duplicateLayer(id: LayerId): LayerId | undefined {
    const d = requireDoc()
    const src = d.layers[id]
    if (!src || src.kind === 'group') return undefined
    const newId = newLayerId()
    mutate('history.duplicateLayer', (draft, px) => {
      const copy = structuredClone(src)
      copy.id = newId
      copy.name = `${src.name} のコピー`
      if (copy.kind === 'raster') {
        const s = pixelStore.require(copy.pixelId).fork(newPixelId())
        px.adopt(s)
        copy.pixelId = s.id
      }
      if (copy.mask) {
        const ms = pixelStore.require(copy.mask.pixelId).fork(newPixelId())
        px.adopt(ms)
        copy.mask.pixelId = ms.id
      }
      insertLayer(draft, copy, id)
    })
    return newId
  }

  /** レイヤー (グループなら子も) を draft から取り除き、表面を捨てる */
  function removeLayerDeep(draft: Document, px: PixelWriter, lid: LayerId): void {
        const layer = draft.layers[lid]
        if (!layer) return
        if (layer.kind === 'group') for (const c of [...layer.children]) removeLayerDeep(draft, px, c)
        const sib = siblingsOf(draft, lid)
        if (sib) sib.splice(sib.indexOf(lid), 1)
        delete draft.layers[lid]
        if (layer.kind === 'text' || layer.kind === 'shape' || layer.kind === 'smart') dropRaster(lid)
        if (layer.kind === 'raster') px.drop(layer.pixelId)
        if (layer.mask) px.drop(layer.mask.pixelId)
  }

  function removeLayers(ids: LayerId[]): void {
    if (!ids.length) return
    mutate('history.deleteLayer', (draft, px) => {
      for (const id of ids) removeLayerDeep(draft, px, id)
      const order = flattenLayerIds(draft)
      draft.activeLayerIds = order.length ? [order[order.length - 1]!] : []
    })
  }

  function setLayerProps(id: LayerId, patch: Partial<Pick<Layer, 'name' | 'visible' | 'opacity' | 'fillOpacity' | 'clipToBelow' | 'lock' | 'labelColor'>> & { blendMode?: BlendMode | GroupBlendMode; blendIf?: BlendIf | null }, label: HistoryLabel | string = 'history.layerProps'): void {
    mutate(label, draft => {
      const l = draft.layers[id]
      if (!l) return
      if (patch.blendMode === 'pass-through' && l.kind !== 'group') return
      const { blendIf, ...rest } = patch
      Object.assign(l, rest)
      if (blendIf === null) delete l.blendIf
      else if (blendIf) l.blendIf = { channel: blendIf.channel, thisLayer: [...blendIf.thisLayer], underlying: [...blendIf.underlying] }
    })
  }

  // --- グループ / マスク / クリッピング ---

  /** 選択レイヤーをグループにまとめる (同じ親に属するものだけ)。戻り値は新グループの ID */
  function groupLayers(ids: LayerId[]): LayerId | undefined {
    if (!ids.length) return undefined
    const d = requireDoc()
    const first = ids[0]!
    const parent = findParentId(d, first)
    if (parent === undefined) return undefined
    const siblings = siblingsOf(d, first)!
    const members = siblings.filter(id => ids.includes(id))
    if (!members.length) return undefined
    const gid = newLayerId()
    mutate('history.groupLayers', draft => {
      const sib = parent === null ? draft.rootChildren : (draft.layers[parent] as { children: LayerId[] }).children
      const topIndex = Math.max(...members.map(m => sib.indexOf(m)))
      const insertAt = topIndex - members.length + 1
      for (const m of members) sib.splice(sib.indexOf(m), 1)
      const g = createGroupLayer({ id: gid, name: nextLayerName(draft, 'グループ'), children: members })
      draft.layers[gid] = g
      sib.splice(insertAt, 0, gid)
      draft.activeLayerIds = [gid]
    })
    return gid
  }

  function ungroupLayer(id: LayerId): void {
    const d = requireDoc()
    const g = d.layers[id]
    if (!g || g.kind !== 'group') return
    mutate('history.ungroupLayers', draft => {
      const sib = siblingsOf(draft, id)
      if (!sib) return
      const at = sib.indexOf(id)
      const children = (draft.layers[id] as { children: LayerId[] }).children
      sib.splice(at, 1, ...children)
      delete draft.layers[id]
      draft.activeLayerIds = [...children]
    })
  }

  function setGroupExpanded(id: LayerId, expanded: boolean): void {
    const d = requireDoc()
    const g = d.layers[id]
    if (!g || g.kind !== 'group' || g.expanded === expanded) return
    // 履歴に積まない UI 状態
    doc.value = { ...d, layers: { ...d.layers, [id]: { ...g, expanded } } }
    emit({ kind: 'view-only' })
  }

  function toggleClip(id: LayerId): void {
    const d = requireDoc()
    const l = d.layers[id]
    if (!l) return
    // 最下層はクリップできない
    const sib = siblingsOf(d, id)
    if (!sib || sib.indexOf(id) === 0) return
    setLayerProps(id, { clipToBelow: !l.clipToBelow }, 'history.clipLayer')
  }

  /**
   * レイヤーマスクを追加する。selectionSurfaceId があればその内容、なければ
   * revealAll (すべて白 = 表示) か hideAll (空 = 非表示)
   */
  function addMask(id: LayerId, mode: 'reveal' | 'hide' = 'reveal'): void {
    const d = requireDoc()
    const l = d.layers[id]
    if (!l || l.mask) return
    mutate('history.addMask', (draft, px) => {
      const s = px.create(newPixelId(), 1)
      if (mode === 'reveal') {
        // 文書全体を白で塗る (タイル境界に揃えて)
        const w = Math.ceil(draft.meta.width / TILE_SIZE) * TILE_SIZE
        const h = Math.ceil(draft.meta.height / TILE_SIZE) * TILE_SIZE
        px.writeRect(s.id, { x: 0, y: 0, w, h }, new Uint8ClampedArray(w * h).fill(255))
      }
      const target = draft.layers[id]!
      target.mask = { pixelId: s.id, enabled: true, linked: true, density: 1, feather: 0, offset: { x: 0, y: 0 } }
    })
  }

  /** 文書サイズのアルファ (0..255) からレイヤーマスクを作る / 置き換える */
  function setLayerMaskFromAlpha(id: LayerId, alpha: Uint8ClampedArray, label: HistoryLabel | string = 'history.setMask'): void {
    const d = requireDoc()
    const l = d.layers[id]
    if (!l || alpha.length !== d.meta.width * d.meta.height) return
    mutate(label, (draft, px) => {
      const target = draft.layers[id]!
      if (target.mask) px.drop(target.mask.pixelId)
      const s = px.create(newPixelId(), 1)
      const w = Math.ceil(draft.meta.width / TILE_SIZE) * TILE_SIZE
      const h = Math.ceil(draft.meta.height / TILE_SIZE) * TILE_SIZE
      const data = new Uint8ClampedArray(w * h)
      for (let y = 0; y < draft.meta.height; y++) data.set(alpha.subarray(y * draft.meta.width, (y + 1) * draft.meta.width), y * w)
      px.writeRect(s.id, { x: 0, y: 0, w, h }, data)
      target.mask = { pixelId: s.id, enabled: true, linked: true, density: 1, feather: 0, offset: { x: 0, y: 0 } }
    })
  }

  function removeMask(id: LayerId): void {
    const d = requireDoc()
    const l = d.layers[id]
    if (!l || !l.mask) return
    mutate('history.removeMask', (draft, px) => {
      const target = draft.layers[id]!
      if (!target.mask) return
      px.drop(target.mask.pixelId)
      delete target.mask
    })
  }

  function setMaskEnabled(id: LayerId, enabled: boolean): void {
    const d = requireDoc()
    const l = d.layers[id]
    if (!l || !l.mask || l.mask.enabled === enabled) return
    mutate('history.toggleMask', draft => {
      const target = draft.layers[id]!
      if (target.mask) target.mask.enabled = enabled
    })
  }

  function toggleVisible(id: LayerId): void {
    const l = requireDoc().layers[id]
    if (!l) return
    const rect = l.kind === 'raster' ? layerRect(id) : null
    mutate('history.toggleVisible', draft => {
      const t = draft.layers[id]
      if (t) t.visible = !t.visible
      return rect && !t?.mask ? { kind: 'rect', rect } : { kind: 'all' }
    })
  }

  /** ラスターレイヤーの位置を絶対値で設定 (ドラッグ中はトランザクション内で呼ぶ) */
  function setLayerOffset(id: LayerId, offset: Pt): void {
    const before = layerRect(id)
    mutate('history.moveLayer', draft => {
      const l = draft.layers[id]
      if (l && l.kind === 'text') {
        l.position = { x: Math.round(offset.x), y: Math.round(offset.y) }
        const after = textRect(id, l)
        const u = unionRect(before, after)
        return u ? { kind: 'rect', rect: u } : { kind: 'all' }
      }
      if (l && (l.kind === 'shape' || l.kind === 'smart')) {
        // シェイプ / スマートオブジェクトの「位置」= 変換行列の平行移動成分
        l.transform = [l.transform[0], l.transform[1], l.transform[2], l.transform[3], l.transform[4], l.transform[5], Math.round(offset.x), Math.round(offset.y), 1]
        const after = l.kind === 'shape' ? shapeRect(id, l) : smartRect(id, l)
        const u = unionRect(before, after)
        return u ? { kind: 'rect', rect: u } : { kind: 'all' }
      }
      if (!l || l.kind !== 'raster') return
      l.offset = { x: Math.round(offset.x), y: Math.round(offset.y) }
      const b = pixelStore.get(l.pixelId)?.contentBounds()
      const after = b ? { x: b.x + l.offset.x, y: b.y + l.offset.y, w: b.w, h: b.h } : null
      const u = unionRect(before, after)
      return u ? { kind: 'rect', rect: u } : { kind: 'view-only' }
    })
  }

  /** レイヤーを別の位置へ移動 (同じ親の中で index を変える、または別の親へ) */
  function reorderLayer(id: LayerId, targetParent: LayerId | null, index: number): void {
    mutate('history.reorderLayer', draft => {
      const sib = siblingsOf(draft, id)
      if (!sib) return
      // 自分自身や子孫の中へは入れない
      let p: LayerId | null | undefined = targetParent
      while (p) {
        if (p === id) return
        p = findParentId(draft, p)
      }
      sib.splice(sib.indexOf(id), 1)
      const target = targetParent === null ? draft.rootChildren : (draft.layers[targetParent] as { children?: LayerId[] } | undefined)?.children
      if (!target) return
      target.splice(Math.max(0, Math.min(index, target.length)), 0, id)
    })
  }

  // --- 選択範囲 ---

  /** 現在の選択範囲を MaskData で返す (なければ null) */
  function selectionMask(): MaskData | null {
    const d = doc.value
    if (!d?.selection.pixelId) return null
    const s = pixelStore.get(d.selection.pixelId)
    return s ? surfaceToMask(s) : null
  }

  const hasSelection = computed(() => {
    void historyVersion.value
    return !!doc.value?.selection.pixelId
  })

  /** 選択範囲を置き換える (null で解除)。文書外は切り落とす */
  function setSelection(mask: MaskData | null, label: HistoryLabel | string = 'history.select'): void {
    const d = requireDoc()
    if (!mask && !d.selection.pixelId) return
    mutate(label, (draft, px) => {
      if (draft.selection.pixelId) {
        px.drop(draft.selection.pixelId)
        draft.selection.pixelId = null
      }
      if (mask) {
        const clipped = combineMasks(mask, { rect: { x: 0, y: 0, w: draft.meta.width, h: draft.meta.height }, data: new Uint8ClampedArray(draft.meta.width * draft.meta.height).fill(255) }, 'intersect')
        if (!clipped) return { kind: 'view-only' }
        const s = maskToSurface(clipped, newPixelId())
        px.adopt(s)
        draft.selection.pixelId = s.id
      }
      return { kind: 'view-only' }
    })
  }

  function combineSelection(next: MaskData | null, mode: CombineMode, label: HistoryLabel | string = 'history.select'): void {
    const cur = selectionMask()
    if (!next) {
      if (mode === 'new') setSelection(null, label)
      return
    }
    setSelection(combineMasks(cur, next, mode), label)
  }

  function selectAll(): void {
    const d = requireDoc()
    setSelection({ rect: { x: 0, y: 0, w: d.meta.width, h: d.meta.height }, data: new Uint8ClampedArray(d.meta.width * d.meta.height).fill(255) }, 'history.selectAll')
  }

  function deselect(): void {
    setSelection(null, 'history.deselect')
  }

  function invertSelection(): void {
    const d = requireDoc()
    setSelection(invertMask(selectionMask(), d.meta.width, d.meta.height), 'history.invertSelection')
  }

  async function modifySelection(kind: 'feather' | 'expand' | 'contract' | 'border', amount: number): Promise<void> {
    const cur = selectionMask()
    if (!cur) return
    const gen = doc.value?.pixelGeneration
    const next = await modifyMaskAsync(cur, kind, amount)
    // 計算中に文書が変わっていたら適用しない
    if (doc.value?.pixelGeneration !== gen) return
    setSelection(next, `history.${kind}Selection`)
  }

  /** 選択範囲内のピクセルを消す (選択がなければレイヤー全体)。アクティブなラスターレイヤーが対象 */
  function clearSelectedPixels(): boolean {
    const d = requireDoc()
    const id = d.activeLayerIds[0]
    const layer = id ? rasterLayer(id) : undefined
    if (!layer || layer.lock.all || layer.lock.pixels) return false
    const surface = pixelStore.get(layer.pixelId)
    if (!surface) return false
    const mask = selectionMask()
    const lb = surface.bounds()
    if (!lb) return false
    mutate('history.clearPixels', (draft, px) => {
      // レイヤー座標での対象矩形
      const target = mask
        ? { x: mask.rect.x - layer.offset.x, y: mask.rect.y - layer.offset.y, w: mask.rect.w, h: mask.rect.h }
        : lb
      const ix = Math.max(target.x, lb.x), iy = Math.max(target.y, lb.y)
      const ex = Math.min(target.x + target.w, lb.x + lb.w), ey = Math.min(target.y + target.h, lb.y + lb.h)
      if (ex <= ix || ey <= iy) return
      const rect = { x: ix, y: iy, w: ex - ix, h: ey - iy }
      const data = surface.readRect(rect)
      const transparencyLocked = layer.lock.transparency
      for (let y = 0; y < rect.h; y++) {
        for (let x = 0; x < rect.w; x++) {
          const m = mask ? maskAt(mask, rect.x + x + layer.offset.x, rect.y + y + layer.offset.y) / 255 : 1
          if (m <= 0) continue
          const o = (y * rect.w + x) * 4
          if (transparencyLocked) {
            // 透明ロック: 背景色 (白) で塗る
            data[o] = Math.round(data[o]! * (1 - m) + 255 * m)
            data[o + 1] = Math.round(data[o + 1]! * (1 - m) + 255 * m)
            data[o + 2] = Math.round(data[o + 2]! * (1 - m) + 255 * m)
          } else {
            data[o + 3] = Math.round(data[o + 3]! * (1 - m))
          }
        }
      }
      px.writeRect(surface.id, rect, data)
      void draft
    })
    return true
  }

  /**
   * rect (文書座標) の RGBA をレイヤーに source-over で合成して書き込む
   * (塗りつぶし / グラデーション / 貼り付けの共通経路)。透明ロックを尊重する。
   */
  function compositeIntoLayer(id: LayerId, rect: Rect, rgba: Uint8ClampedArray, label: HistoryLabel | string, alphaScale = 1): boolean {
    const layer = rasterLayer(id)
    if (!layer || layer.lock.all || layer.lock.pixels) return false
    const surface = pixelStore.get(layer.pixelId)
    if (!surface || rect.w <= 0 || rect.h <= 0) return false
    const local = { x: rect.x - layer.offset.x, y: rect.y - layer.offset.y, w: rect.w, h: rect.h }
    mutate(label, (draft, px) => {
      const dst = surface.readRect(local)
      sourceOverInPlace(dst, rgba, alphaScale, layer.lock.transparency)
      px.writeRect(surface.id, local, dst)
      void draft
      return { kind: 'rect', rect }
    })
    return true
  }

  /** レイヤー座標の矩形をそのまま置き換える (ゆがみなど、計算済みの画素を書き戻す) */
  function writeLayerRect(id: LayerId, local: Rect, rgba: Uint8ClampedArray, label: HistoryLabel | string): boolean {
    const layer = rasterLayer(id)
    if (!layer || layer.lock.all || layer.lock.pixels) return false
    const surface = pixelStore.get(layer.pixelId)
    if (!surface || local.w <= 0 || local.h <= 0 || rgba.length !== local.w * local.h * 4) return false
    const docRect = { x: local.x + layer.offset.x, y: local.y + layer.offset.y, w: local.w, h: local.h }
    mutate(label, (draft, px) => {
      px.writeRect(surface.id, local, rgba)
      void draft
      return { kind: 'rect', rect: docRect }
    })
    return true
  }

  // --- 幾何操作 (切り抜き / カンバスサイズ / 画像解像度 / 回転 / 反転 / 変形) ---

  /** 表面の内容を RgbaImage として取り出す (contentBounds 範囲)。空なら null */
  function surfaceImage(pixelId: string): { img: RgbaImage; rect: Rect } | null {
    const s = pixelStore.get(pixelId)
    const b = s?.contentBounds()
    if (!s || !b) return null
    return { img: { width: b.w, height: b.h, data: s.readRect(b) }, rect: b }
  }

  /** レイヤー (またはマスク) の表面を新しい画像で置き換える。旧表面は履歴が保持する */
  function replaceSurface(px: PixelWriter, oldId: string, img: RgbaImage | null, origin: Pt, channels: 1 | 4): string {
    px.drop(oldId)
    const s = px.create(newPixelId(), channels)
    if (img && img.width > 0 && img.height > 0) {
      const data = channels === 4 ? img.data : rgbaToGray(img.data)
      px.writeRect(s.id, { x: origin.x, y: origin.y, w: img.width, h: img.height }, data)
    }
    return s.id
  }

  type PixelOp = (img: RgbaImage) => RgbaImage
  type RectOp = (rect: Rect, img: RgbaImage) => Pt

  /**
   * 全ラスターレイヤー (とマスク) に画素操作を適用する。
   * op: 画像変換、place: 変換後の画像の文書座標原点
   */
  function transformAllLayers(draft: Document, px: PixelWriter, op: PixelOp, place: (docRect: Rect, out: RgbaImage) => Pt) {
    for (const layer of Object.values(draft.layers)) {
      if (layer.kind === 'raster') {
        const si = surfaceImage(layer.pixelId)
        if (si) {
          const docRect = { x: si.rect.x + layer.offset.x, y: si.rect.y + layer.offset.y, w: si.rect.w, h: si.rect.h }
          const out = op(si.img)
          const origin = place(docRect, out)
          layer.pixelId = replaceSurface(px, layer.pixelId, out, { x: 0, y: 0 }, 4)
          layer.offset = origin
        } else {
          layer.pixelId = replaceSurface(px, layer.pixelId, null, { x: 0, y: 0 }, 4)
          layer.offset = { x: 0, y: 0 }
        }
      }
      if (layer.mask) {
        const ms = pixelStore.get(layer.mask.pixelId)
        const b = ms?.contentBounds()
        if (ms && b) {
          const gray = ms.readRect(b)
          const img = op({ width: b.w, height: b.h, data: grayToRgba(gray) })
          const origin = place({ x: b.x + layer.mask.offset.x, y: b.y + layer.mask.offset.y, w: b.w, h: b.h }, img)
          layer.mask.pixelId = replaceSurface(px, layer.mask.pixelId, img, { x: 0, y: 0 }, 1)
          layer.mask.offset = origin
        }
      }
    }
    if (draft.selection.pixelId) {
      px.drop(draft.selection.pixelId)
      draft.selection.pixelId = null
    }
  }

  /** 文書を rect に切り抜く。rect 外の画素は保持する (オフセットをずらすだけ) */
  function cropDocument(rect: Rect): void {
    const d = requireDoc()
    const r = { x: Math.round(rect.x), y: Math.round(rect.y), w: Math.max(1, Math.round(rect.w)), h: Math.max(1, Math.round(rect.h)) }
    if (r.x === 0 && r.y === 0 && r.w === d.meta.width && r.h === d.meta.height) return
    mutate('history.crop', (draft, px) => {
      draft.meta.width = r.w
      draft.meta.height = r.h
      for (const layer of Object.values(draft.layers)) {
        if (layer.kind === 'raster') layer.offset = { x: layer.offset.x - r.x, y: layer.offset.y - r.y }
        if (layer.mask) layer.mask.offset = { x: layer.mask.offset.x - r.x, y: layer.mask.offset.y - r.y }
      }
      for (const g of draft.guides) g.position -= g.axis === 'x' ? r.x : r.y
      if (draft.selection.pixelId) {
        px.drop(draft.selection.pixelId)
        draft.selection.pixelId = null
      }
    })
  }

  /** カンバスサイズ変更 (画素はそのまま、アンカーに応じてずらす) */
  function resizeCanvas(w: number, h: number, anchor: Anchor = 'c'): void {
    const d = requireDoc()
    const nw = Math.max(1, Math.round(w)), nh = Math.max(1, Math.round(h))
    if (nw === d.meta.width && nh === d.meta.height) return
    const ax = anchor.includes('l') ? 0 : anchor.includes('r') ? 1 : 0.5
    const ay = anchor.startsWith('t') ? 0 : anchor.startsWith('b') ? 1 : 0.5
    const dx = Math.round((nw - d.meta.width) * ax)
    const dy = Math.round((nh - d.meta.height) * ay)
    cropDocument({ x: -dx, y: -dy, w: nw, h: nh })
  }

  /** 画像解像度 (全レイヤーを再サンプル) */
  function resizeImage(w: number, h: number, method: Interpolation = 'bicubic'): void {
    const d = requireDoc()
    const nw = Math.max(1, Math.round(w)), nh = Math.max(1, Math.round(h))
    if (nw === d.meta.width && nh === d.meta.height) return
    const sx = nw / d.meta.width, sy = nh / d.meta.height
    mutate('history.imageSize', (draft, px) => {
      draft.meta.width = nw
      draft.meta.height = nh
      transformAllLayers(
        draft,
        px,
        img => resizeRgba(img, Math.max(1, Math.round(img.width * sx)), Math.max(1, Math.round(img.height * sy)), method),
        docRect => ({ x: Math.round(docRect.x * sx), y: Math.round(docRect.y * sy) })
      )
      for (const g of draft.guides) g.position *= g.axis === 'x' ? sx : sy
    })
  }

  function rotateCanvas(deg: 90 | 180 | 270): void {
    const d = requireDoc()
    const W = d.meta.width, H = d.meta.height
    mutate('history.rotateCanvas', (draft, px) => {
      if (deg !== 180) {
        draft.meta.width = H
        draft.meta.height = W
      }
      transformAllLayers(
        draft,
        px,
        img => (deg === 90 ? rotate90(img) : deg === 180 ? rotate180(img) : rotate270(img)),
        (r, out) => {
          if (deg === 90) return { x: H - (r.y + r.h), y: r.x }
          if (deg === 180) return { x: W - (r.x + r.w), y: H - (r.y + r.h) }
          void out
          return { x: r.y, y: W - (r.x + r.w) }
        }
      )
      draft.guides = []
    })
  }

  function flipCanvas(axis: 'h' | 'v'): void {
    const d = requireDoc()
    const W = d.meta.width, H = d.meta.height
    mutate('history.flipCanvas', (draft, px) => {
      transformAllLayers(draft, px, img => (axis === 'h' ? flipH(img) : flipV(img)), r => (axis === 'h' ? { x: W - (r.x + r.w), y: r.y } : { x: r.x, y: H - (r.y + r.h) }))
    })
  }

  /** アクティブなラスターレイヤー 1 枚を反転 / 90° 回転 (内容の中心を保つ) */
  function transformLayerSimple(id: LayerId, op: 'flipH' | 'flipV' | 'rot90' | 'rot180' | 'rot270'): void {
    const layer = rasterLayer(id)
    if (!layer || layer.lock.all || layer.lock.pixels) return
    const si = surfaceImage(layer.pixelId)
    if (!si) return
    mutate('history.transformLayer', (draft, px) => {
      const l = draft.layers[id] as RasterLayer
      const out = op === 'flipH' ? flipH(si.img) : op === 'flipV' ? flipV(si.img) : op === 'rot90' ? rotate90(si.img) : op === 'rot180' ? rotate180(si.img) : rotate270(si.img)
      const cx = si.rect.x + l.offset.x + si.rect.w / 2
      const cy = si.rect.y + l.offset.y + si.rect.h / 2
      l.pixelId = replaceSurface(px, l.pixelId, out, { x: 0, y: 0 }, 4)
      l.offset = { x: Math.round(cx - out.width / 2), y: Math.round(cy - out.height / 2) }
      if (l.mask) {
        const ms = pixelStore.get(l.mask.pixelId)
        const b = ms?.contentBounds()
        if (ms && b) {
          const mimg = { width: b.w, height: b.h, data: grayToRgba(ms.readRect(b)) }
          const mout = op === 'flipH' ? flipH(mimg) : op === 'flipV' ? flipV(mimg) : op === 'rot90' ? rotate90(mimg) : op === 'rot180' ? rotate180(mimg) : rotate270(mimg)
          const mcx = b.x + l.mask.offset.x + b.w / 2, mcy = b.y + l.mask.offset.y + b.h / 2
          l.mask.pixelId = replaceSurface(px, l.mask.pixelId, mout, { x: 0, y: 0 }, 1)
          l.mask.offset = { x: Math.round(mcx - mout.width / 2), y: Math.round(mcy - mout.height / 2) }
        }
      }
    })
  }

  /**
   * 自由変形の確定: レイヤー内容 (文書座標) に行列 m を適用して再サンプルする。
   * m は文書座標 → 文書座標。
   */
  function transformLayer(id: LayerId, m: Mat3, method: Interpolation = 'bicubic'): void {
    const shapeL = doc.value?.layers[id]
    if (shapeL && shapeL.kind === 'shape') {
      // シェイプは幾何を保ち、変換行列に合成する (非破壊)
      setShape(id, { transform: mat3Multiply(m, shapeL.transform) }, 'history.freeTransform')
      return
    }
    if (shapeL && shapeL.kind === 'smart') {
      if (shapeL.lock.all) return
      setSmart(id, { transform: mat3Multiply(m, shapeL.transform) }, 'history.freeTransform')
      return
    }
    const layer = rasterLayer(id)
    if (!layer || layer.lock.all || layer.lock.pixels) return
    const si = surfaceImage(layer.pixelId)
    if (!si) return
    const inv = mat3Invert(m)
    if (!inv) return
    const docRect = { x: si.rect.x + layer.offset.x, y: si.rect.y + layer.offset.y, w: si.rect.w, h: si.rect.h }
    const bounds = transformedBounds(docRect, m)
    // dst 文書座標 → src 画像座標: 逆行列で文書座標へ戻し、画像原点を引く
    const toImg = mat3Multiply(mat3Translate(-docRect.x, -docRect.y), inv)
    mutate('history.freeTransform', (draft, px) => {
      const l = draft.layers[id] as RasterLayer
      const out = resampleAffine(si.img, bounds, toImg, method)
      l.pixelId = replaceSurface(px, l.pixelId, out, { x: 0, y: 0 }, 4)
      l.offset = { x: bounds.x, y: bounds.y }
      if (l.mask && l.mask.linked) {
        const ms = pixelStore.get(l.mask.pixelId)
        const b = ms?.contentBounds()
        if (ms && b) {
          const mRect = { x: b.x + l.mask.offset.x, y: b.y + l.mask.offset.y, w: b.w, h: b.h }
          const mb = transformedBounds(mRect, m)
          const mimg = { width: b.w, height: b.h, data: grayToRgba(ms.readRect(b)) }
          const mout = resampleAffine(mimg, mb, mat3Multiply(mat3Translate(-mRect.x, -mRect.y), inv), method)
          l.mask.pixelId = replaceSurface(px, l.mask.pixelId, mout, { x: 0, y: 0 }, 1)
          l.mask.offset = { x: mb.x, y: mb.y }
        }
      }
    })
  }

  function rasterLayer(id: LayerId): RasterLayer | undefined {
    const l = doc.value?.layers[id]
    return l && l.kind === 'raster' ? l : undefined
  }

  /** レイヤーの文書座標での矩形 (不透明ピクセルの範囲 + offset)。空なら null */
  function layerRect(id: LayerId): Rect | null {
    void historyVersion.value
    const tl = doc.value?.layers[id]
    if (tl && tl.kind === 'text') return textRect(id, tl)
    if (tl && tl.kind === 'shape') return shapeRect(id, tl)
    if (tl && tl.kind === 'smart') return smartRect(id, tl)
    const l = rasterLayer(id)
    if (!l) return null
    const b = pixelStore.get(l.pixelId)?.contentBounds()
    return b ? { x: b.x + l.offset.x, y: b.y + l.offset.y, w: b.w, h: b.h } : null
  }

  return {
    doc,
    hasDocument,
    canUndo,
    canRedo,
    isDirty,
    signature,
    sessionVersion,
    historyVersion,
    activeLayer,
    onChange,
    mutate,
    beginTransaction,
    commitTransaction,
    rollbackTransaction,
    transaction,
    undo,
    redo,
    historyEntries,
    replaceDocument,
    closeDocument,
    newDocument,
    markSaved,
    setActiveLayers,
    addRasterLayerFromRgba,
    addEmptyLayer,
    addAdjustmentLayer,
    setAdjustment,
    addFillLayer,
    setFill,
    setStyles,
    contentAwareFillSelection,
    replaceLayersWithRaster,
    replaceLayerImage,
    selectionFromShapeGeometry,
    setColorLookup,
    addColorLookupLayer,
    definePattern,
    removePattern,
    fillSelectionWithPattern,
    addLayerComp,
    updateLayerComp,
    applyLayerComp,
    removeLayerComp,
    renameLayerComp,
    snapshots,
    takeSnapshot,
    restoreSnapshot,
    deleteSnapshot,
    newLayerViaCopy,
    fillSelectionWithColor,
    strokeSelection,
    trimTransparent,
    rotateCanvasArbitrary,
    autoLevels,
    selectColorRange,
    refineSelection,
    smartRect,
    convertToSmartObject,
    rasterizeSmartLayer,
    setSmart,
    resetSmartTransform,
    replaceSmartContents,
    alignLayers,
    distributeLayers,
    layerOrigin,
    saveSelectionAsChannel,
    loadChannelAsSelection,
    removeChannel,
    renameChannel,
    paintSelection,
    addGuide,
    moveGuide,
    removeGuide,
    clearGuides,
    addShapeLayer,
    setShape,
    shapeRect,
    rasterizeShapeLayer,
    selectionFromLayerAlpha,
    addTextLayer,
    setText,
    textRect,
    rasterizeTextLayer,
    duplicateLayer,
    removeLayers,
    setLayerProps,
    groupLayers,
    ungroupLayer,
    setGroupExpanded,
    toggleClip,
    addMask,
    setLayerMaskFromAlpha,
    removeMask,
    setMaskEnabled,
    toggleVisible,
    setLayerOffset,
    reorderLayer,
    rasterLayer,
    layerRect,
    selectionMask,
    hasSelection,
    setSelection,
    combineSelection,
    selectAll,
    deselect,
    invertSelection,
    modifySelection,
    clearSelectedPixels,
    compositeIntoLayer,
    writeLayerRect,
    surfaceImage,
    cropDocument,
    resizeCanvas,
    resizeImage,
    rotateCanvas,
    flipCanvas,
    transformLayerSimple,
    transformLayer
  }
})

const ADJ_NAMES: Record<AdjustmentKind, string> = {
  levels: 'レベル補正', curves: 'トーンカーブ', 'brightness-contrast': '明るさ・コントラスト', exposure: '露光量', 'hue-saturation': '色相・彩度',
  'color-balance': 'カラーバランス', 'black-white': '白黒', 'photo-filter': 'レンズフィルター', 'channel-mixer': 'チャンネルミキサー', invert: '階調の反転',
  posterize: 'ポスタリゼーション', threshold: '2 階調化', 'gradient-map': 'グラデーションマップ', vibrance: '自然な彩度', 'selective-color': '特定色域の選択', 'color-lookup': 'カラールックアップ'
}
function adjustmentDefaultName(kind: AdjustmentKind): string {
  return ADJ_NAMES[kind]
}

function unionRect(a: Rect | null, b: Rect | null): Rect | null {
  if (!a) return b
  if (!b) return a
  const x0 = Math.min(a.x, b.x), y0 = Math.min(a.y, b.y)
  const x1 = Math.max(a.x + a.w, b.x + b.w), y1 = Math.max(a.y + a.h, b.y + b.h)
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }
}

function rgbaToGray(rgba: Uint8ClampedArray): Uint8ClampedArray {
  const out = new Uint8ClampedArray(rgba.length / 4)
  for (let i = 0; i < out.length; i++) out[i] = rgba[i * 4]!
  return out
}

function grayToRgba(gray: Uint8ClampedArray): Uint8ClampedArray {
  const out = new Uint8ClampedArray(gray.length * 4)
  for (let i = 0; i < gray.length; i++) {
    const v = gray[i]!
    out[i * 4] = v; out[i * 4 + 1] = v; out[i * 4 + 2] = v; out[i * 4 + 3] = 255
  }
  return out
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}
